import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {environment} from '../../env';
import {AppLogger, AppLoggerService} from '../../core/logging';
import {MealPlanGateway} from './meal-plan-gateway';
import {EntryDraft, EntryPatch, MealPlanDay, MealPlanEntry, MealSource, PoolItemDraft, RecipeSearchResult, Slot} from './meal-plan.models';
import {MealPlanSlot, SlotListResponse} from './meal-plan-slot.model';

/**
 * Transport for the meal planner, written against the shipped phase-1 REST API
 * (slot catalogue, range read, entry create/update/delete).
 *
 * This is the ONLY place snake_case ↔ camelCase mapping happens; everything above
 * the gateway speaks the view model. Note the endpoint split: reads/creates are
 * household-scoped, edits/deletes are entry-addressed (`/meal-plan-entries/{id}`).
 */

// --- API wire shapes (snake_case, as the backend returns them) ---

interface ApiRecipe {
  id: number;
  title?: string;
  servings: number;
  prep_time_minutes?: number;
  total_time_minutes?: number;
}

interface ApiEntry {
  id: number;
  client_id?: string; // present only if one was supplied on create
  plan_date?: string | null; // null for pool items (unplaced); present on placed single-entry responses
  slot?: Slot | null; // null for pool items (unplaced)
  sort_order: number;
  servings?: number | null; // omitted when null
  note?: string; // omitted when empty
  title: string;
  portion_of_entry_id: number | null;
  recipe: ApiRecipe | null;
  source?: MealSource; // 'manual' when omitted
}

interface ApiDay {
  date: string;
  entries: ApiEntry[];
}

interface ApiRangeResponse {
  range: {from: string; to: string};
  days: ApiDay[];
}

interface ApiPoolResponse {
  items: ApiEntry[];
}

interface ApiRecipeListItem {
  id: number;
  title: string;
  servings?: number;
  prep_time_minutes?: number;
  total_time_minutes?: number;
}

function entryFromApi(e: ApiEntry): MealPlanEntry {
  return {
    id: e.id,
    clientId: e.client_id,
    planDate: e.plan_date ?? '', // '' for pool items; range reads fill this from the enclosing day
    slot: e.slot ?? '', // '' for pool items (unplaced)
    sortOrder: e.sort_order,
    servings: e.servings ?? null,
    note: e.note,
    title: e.title,
    portionOfEntryId: e.portion_of_entry_id,
    recipe: e.recipe
      ? {
          id: e.recipe.id,
          servings: e.recipe.servings,
          prepTimeMinutes: e.recipe.prep_time_minutes,
          totalTimeMinutes: e.recipe.total_time_minutes,
        }
      : null,
    source: e.source ?? 'manual',
  };
}

@Injectable({providedIn: 'root'})
export class HttpMealPlanGateway implements MealPlanGateway {
  private readonly log: AppLogger;

  constructor(private http: HttpClient, appLogger: AppLoggerService) {
    this.log = appLogger.createForClass(HttpMealPlanGateway);
  }

  getSlots(householdId: number): Observable<MealPlanSlot[]> {
    this.log.debug('Get meal-plan slots', {householdId});
    return this.http
      .get<SlotListResponse>(`${environment.apiUrl}/households/${householdId}/meal-plan/slots`)
      .pipe(map((r) => [...r.slots].sort((a, b) => a.order - b.order)));
  }

  getRange(householdId: number, from: string, to: string): Observable<MealPlanDay[]> {
    this.log.debug('Get meal-plan range', {householdId, from, to});
    return this.http
      .get<ApiRangeResponse>(`${environment.apiUrl}/households/${householdId}/meal-plan`, {
        params: {from, to},
      })
      .pipe(
        map((res) =>
          res.days.map((d) => ({
            date: d.date,
            entries: d.entries.map((e) => ({...entryFromApi(e), planDate: d.date})),
          })),
        ),
      );
  }

  createEntry(householdId: number, draft: EntryDraft): Observable<MealPlanEntry> {
    this.log.debug('Create meal-plan entry', {householdId, draft});
    // The API expects exactly one of recipe_id / title / portion_of_entry_id.
    const body: Record<string, unknown> = {
      plan_date: draft.planDate,
      slot: draft.slot,
    };
    if (draft.recipeId != null) {
      body['recipe_id'] = draft.recipeId;
    } else if (draft.title != null) {
      body['title'] = draft.title;
    }
    if (draft.servings != null) body['servings'] = draft.servings;
    if (draft.note) body['note'] = draft.note;
    if (draft.clientId) body['client_id'] = draft.clientId;

    return this.http
      .post<ApiEntry>(`${environment.apiUrl}/households/${householdId}/meal-plan/entries`, body)
      .pipe(map((e) => ({...entryFromApi(e), planDate: e.plan_date ?? draft.planDate})));
  }

  updateEntry(id: number, patch: EntryPatch): Observable<MealPlanEntry> {
    this.log.debug('Update meal-plan entry', {id, patch});
    const body: Record<string, unknown> = {};
    if (patch.servings !== undefined) body['servings'] = patch.servings;
    if (patch.note !== undefined) body['note'] = patch.note;
    if (patch.planDate !== undefined) body['plan_date'] = patch.planDate;
    if (patch.slot !== undefined) body['slot'] = patch.slot;
    if (patch.recipeId !== undefined) body['recipe_id'] = patch.recipeId;
    // Entry-addressed: PATCH /meal-plan-entries/{id} (no household segment).
    return this.http
      .patch<ApiEntry>(`${environment.apiUrl}/meal-plan-entries/${id}`, body)
      .pipe(map((e) => entryFromApi(e)));
  }

  deleteEntry(id: number): Observable<void> {
    this.log.debug('Delete meal-plan entry', {id});
    return this.http.delete<void>(`${environment.apiUrl}/meal-plan-entries/${id}`);
  }

  getPool(householdId: number): Observable<MealPlanEntry[]> {
    this.log.debug('Get meal-plan pool', {householdId});
    return this.http
      .get<ApiPoolResponse>(`${environment.apiUrl}/households/${householdId}/meal-plan/pool`)
      .pipe(map((res) => res.items.map(entryFromApi)));
  }

  generatePool(householdId: number, count?: number, theme?: string): Observable<MealPlanEntry[]> {
    this.log.debug('Generate meal-plan pool', {householdId, count, theme});
    const body: Record<string, unknown> = {};
    if (count != null) body['count'] = count;
    if (theme) body['theme'] = theme;
    // GenerateResult mirrors the pool response shape ({items: ApiEntry[]}).
    return this.http
      .post<ApiPoolResponse>(`${environment.apiUrl}/households/${householdId}/meal-plan/generate`, body)
      .pipe(map((res) => (res.items ?? []).map(entryFromApi)));
  }

  createPoolItem(householdId: number, draft: PoolItemDraft): Observable<MealPlanEntry> {
    this.log.debug('Create meal-plan pool item', {householdId, draft});
    // Like createEntry but without plan_date/slot — that's what "pooled" means.
    const body: Record<string, unknown> = {};
    if (draft.recipeId != null) {
      body['recipe_id'] = draft.recipeId;
    } else if (draft.title != null) {
      body['title'] = draft.title;
    }
    if (draft.servings != null) body['servings'] = draft.servings;
    if (draft.note) body['note'] = draft.note;
    if (draft.source) body['source'] = draft.source;
    if (draft.clientId) body['client_id'] = draft.clientId;

    return this.http
      .post<ApiEntry>(`${environment.apiUrl}/households/${householdId}/meal-plan/pool`, body)
      .pipe(map(entryFromApi));
  }

  unplaceEntry(id: number): Observable<MealPlanEntry> {
    this.log.debug('Unplace meal-plan entry', {id});
    // Dedicated verb: clears plan_date + slot in one gesture, sidestepping the
    // omitted-vs-null ambiguity of a PATCH body.
    return this.http
      .post<ApiEntry>(`${environment.apiUrl}/meal-plan-entries/${id}/unplace`, {})
      .pipe(map(entryFromApi));
  }

  searchRecipes(householdId: number, query: string): Observable<RecipeSearchResult[]> {
    this.log.debug('Search recipes', {householdId, query});
    // Reuses the household recipe list endpoint with the `q` search parameter —
    // the same server-side filter the recipes list page uses.
    return this.http
      .get<ApiRecipeListItem[]>(`${environment.apiUrl}/households/${householdId}/recipes`, {
        params: {q: query},
      })
      .pipe(
        map((items) =>
          items.map((r) => ({
            id: r.id,
            title: r.title,
            servings: r.servings ?? 1,
            prepTimeMinutes: r.prep_time_minutes,
            totalTimeMinutes: r.total_time_minutes,
          })),
        ),
      );
  }
}
